import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { Customer, CustomerInsert, CustomerUpdate, CustomerWithActivity } from '@/lib/integrations/supabase/types';
import { toE164, extractDigits, DEFAULT_COUNTRY_CODE } from '@/lib/utils/phone';

export async function getCustomers(businessId: string): Promise<Customer[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function searchCustomers(
  businessId: string,
  query: string
): Promise<Customer[]> {
  const supabase = getBrowserClient();
  const searchTerm = `%${query}%`;

  const searchConditions = [`name.ilike.${searchTerm}`, `email.ilike.${searchTerm}`];

  const digits = extractDigits(query);
  if (digits && digits.length >= 3) {
    const e164Phone = toE164(query, DEFAULT_COUNTRY_CODE);
    if (e164Phone) {
      searchConditions.push(`normalized_phone.eq.${e164Phone}`);
    }
    searchConditions.push(`normalized_phone.ilike.%${digits}%`);
  }

  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .or(searchConditions.join(','))
    .order('name', { ascending: true })
    .limit(20);

  if (error) {
    throw error;
  }

  return data || [];
}

export async function getCustomer(id: string): Promise<Customer | null> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

export async function getCustomersWithActivity(
  businessId: string
): Promise<CustomerWithActivity[]> {
  const supabase = getBrowserClient();

  const { data, error } = await (supabase as any)
    .rpc('get_customer_activity', { p_business_id: businessId });

  if (error) {
    throw error;
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    business_id: businessId,
    name: row.name,
    phone: row.phone,
    email: row.email,
    notes: null,
    created_at: new Date().toISOString(),
    total_visits: Number(row.total_visits),
    next_booking: row.next_visit_date ? { start_at: row.next_visit_date } as any : null,
    last_booking: row.last_visit_date ? { start_at: row.last_visit_date } as any : null,
  }));
}

export async function getCustomersByStaff(
  businessId: string,
  staffId: string
): Promise<CustomerWithActivity[]> {
  const supabase = getBrowserClient();

  const { data, error } = await (supabase as any)
    .rpc('get_customers_by_staff', {
      p_business_id: businessId,
      p_staff_id: staffId,
    });

  if (error) {
    throw error;
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    business_id: businessId,
    name: row.name,
    phone: row.phone,
    email: row.email,
    notes: null,
    created_at: new Date().toISOString(),
    total_visits: Number(row.total_visits),
    next_booking: row.next_visit_date ? { start_at: row.next_visit_date } as any : null,
    last_booking: row.last_visit_date ? { start_at: row.last_visit_date } as any : null,
  }));
}

export interface CreateCustomerData {
  businessId: string;
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
  /** ISO country code for phone normalization (e.g., 'AE', 'SA'). Defaults to 'AE'. */
  countryCode?: string;
}

export async function createCustomer(data: CreateCustomerData): Promise<Customer> {
  const response = await fetch('/api/customers/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create customer');
  }

  return response.json();
}

export async function updateCustomer(
  customerId: string,
  data: CustomerUpdate
): Promise<Customer> {
  const response = await fetch(`/api/customers/${customerId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update customer');
  }

  return response.json();
}
